<template>
  <main-page :user="user" color="green">
    <template v-slot:title> PITCH PROGRESS </template>

    <div class="container">
      <div v-if="!loading && (user.role != 'STUDENT' || team)" class="row content">
        <div class="col-4 zone-padding pt-4 text-left">
          <h1 class="mb-2 si-font si-text-orange">Team</h1>
          <config-select
            v-if="user.role != 'STUDENT'"
            class="vs-big mb-4"
            :options="teams"
            label="name"
            :reduce="(team) => team.id"
            v-model="team"
          />
          <config-select
            v-if="user.role == 'STUDENT'"
            class="vs-big mb-4"
            :options="[team]"
            label="name"
            :reduce="(team) => team.id"
            v-model="team"
            disabled
          />

          <h1 class="mt-4 mb-2 si-font si-text-green">Category</h1>
          <v-select
            class="vs-big"
            :options="categoryOptions"
            label="name"
            :clearable="false"
            v-model="category"
          />

          <h1 class="mt-4 mb-2 si-font si-text-blue">Feedback Type</h1>
          <v-select
            class="vs-big"
            :options="types"
            :clearable="false"
            v-model="type"
            :reduce="(obj) => obj.id"
          />

          <div v-if="user.role != 'JUDGE'">
            <h1 class="mt-4 mb-2 si-font si-text-yellow">Feedback Group</h1>
            <div
              class="mb-2"
              v-if="
                !context.is_contest ||
                (program.contest_config && program.contest_config.peer_feedback)
              "
            >
              <label class="md-check check-lg">
                <input type="checkbox" checked="checked" v-model="peer" />
                <i class="yellow"></i> Peer
              </label>
            </div>
            <div class="mb-2">
              <label class="md-check check-lg">
                <input type="checkbox" checked="checked" v-model="educator" />
                <i class="yellow"></i> Educator
              </label>
            </div>
            <div class="mb-2" v-if="!context.is_contest">
              <label class="md-check check-lg">
                <input type="checkbox" checked="checked" v-model="partner" />
                <i class="yellow"></i> Community Partner
              </label>
            </div>
            <div class="mb-2" v-if="context.is_contest">
              <label class="md-check check-lg">
                <input type="checkbox" checked="checked" v-model="judge" />
                <i class="yellow"></i> Judge
              </label>
            </div>
          </div>
        </div>
        <div
          v-if="category && type == 'scores'"
          class="col-8 zone-padding text-left d-flex flex-column justify-content-center h-100"
        >
          <template v-if="chartData && chartData.length > 0">
            <apex-chart
              v-if="chartData.length <= 100"
              ref="chart"
              type="line"
              :options="chartOptions"
              :series="chartData"
              height="500px;"
            ></apex-chart>
            <div v-if="chartData.length > 100" class="card p-5 mb-5">
              <div class="text-center text-very-muted text-lg _800">
                Too much data for line graph
              </div>
              <div class="text-center text-very-muted text-lg _800">
                Export to view data
              </div>
            </div>
            <div class="ml-5 px-4 py-2 text-md border">
              <div class="text-center">
                <h3 class="my-0 si-font text-center" style="display: inline-block">
                  STATISTICS
                </h3>
                <span>, {{ getTitle() }}</span>
              </div>
              <div>
                <strong>Range: </strong>
                <span v-if="rangeLow != rangeHigh"
                  >{{ fs.round1d(rangeLow) }} to {{ fs.round1d(rangeHigh) }}</span
                >
                <span v-if="rangeLow == rangeHigh">{{ fs.round1d(rangeLow) }}</span>
                -
                <span
                  v-if="
                    fs.pitchScoreDescription(rangeLow) !=
                    fs.pitchScoreDescription(rangeHigh)
                  "
                >
                  "{{ fs.pitchScoreDescription(rangeLow) }}" to "{{
                    fs.pitchScoreDescription(rangeHigh)
                  }}"
                </span>
                <span
                  v-if="
                    fs.pitchScoreDescription(rangeLow) ==
                    fs.pitchScoreDescription(rangeHigh)
                  "
                >
                  "{{ fs.pitchScoreDescription(rangeLow) }}"
                </span>
              </div>
              <div class="d-flex flex-row justify-content-between">
                <div class="d-flex flex-row align-items-center">
                  <strong>Mean:</strong><span class="ml-1">{{ fs.round1d(mean) }}</span>
                </div>
                <div class="d-flex flex-row align-items-center ml-3">
                  <strong>Median:</strong
                  ><span class="ml-1">{{ fs.round1d(median) }}</span>
                </div>
                <div class="d-flex flex-row align-items-center ml-3">
                  <template v-if="category && category.id != 'all'">
                    <strong>Mode:</strong><span class="ml-1">{{ mode }}</span>
                  </template>
                </div>
              </div>
            </div>
          </template>
          <div v-if="!chartData || chartData.length == 0" class="card p-5">
            <div class="text-center text-very-muted text-lg _800">No feedback</div>
          </div>
        </div>

        <div
          v-if="category && type == 'explanations'"
          class="col-8 zone-padding text-left d-flex flex-column justify-content-center h-100"
        >
          <div class="text-left">
            <template v-for="block in explanations">
              <div :key="`header-${block.assignment_id}`" class="table-head border">
                <h2 class="my-0 si-font">{{ block.assignment_name }}</h2>
                <div
                  class="my-0 si-font text-lg text-muted"
                  style="line-height: 20px; padding-bottom: 4px"
                >
                  {{ category.name }} Feedback
                </div>
              </div>
              <table
                :key="`table-${block.assignment_id}`"
                class="table table-striped border text-md mb-1"
              >
                <thead>
                  <tr>
                    <th>Reviewer</th>
                    <th class="text-center">Score</th>
                    <th>Feedback</th>
                    <th v-if="user.role == 'TEACHER'"></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Students -->
                  <tr v-for="(row, i) in block.explanations" :key="i">
                    <td class="v-mid" style="min-width: 120px">
                      <div style="line-height: 1.2">
                        {{ getFeedbackName(row.user, i) }}
                      </div>
                      <div class="si-font text-muted" style="line-height: 1">
                        {{ row.user.role }}
                      </div>
                    </td>
                    <td class="text-lg v-mid text-center">{{ row.score }}</td>
                    <td class="v-mid">{{ row.text }}</td>
                    <td v-if="user.role == 'TEACHER'">
                      <button
                        class="mt-1 btn btn-no-border btn-si-icon float-right"
                        v-tippy
                        title="Delete"
                      >
                        <i
                          class="fas fa-trash"
                          @click="openDeleteModal(row.user)"
                        />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                class="d-flex flex-column align-items-end mb-4"
                :key="`table-${block.assignment_id}`"
              >
                <div>Note: {{ block.explanations.length }} responses</div>
                <div>
                  {{ getExplanationCountDetails(block) }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div v-if="!loading && user.role == 'STUDENT' && !team">
        <h1 class="text-extra-muted mt-5" style="font-size: 64px">No Team</h1>
        <h3 class="text-extra-muted">
          You must be on a team in this class to view pitch results.<br />Please contact
          your teacher to put you on a team.
        </h3>
      </div>
    </div>

    <b-modal id="deleteFeedbackModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete Pitch Feedback</h3>
      </template>
      <div v-if="deletingFeedback" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete these Pitch Feedback responses from
            <strong class="text-theme text-center">{{ fs.displayName(deletingFeedback.user.name) }}</strong>?
          </h5>
          <table>
            <tr>
              <td class="text-sm b-t b-r py-2 px-3">Category</td>
              <td class="text-sm b-t b-r py-2 px-3">Score</td>
              <td class="text-sm b-t py-2 px-3">Text</td>
            </tr>
            <tr v-for="(resp, idx) in deletingFeedback.response.responses" :key="resp.id">
              <td class="text-sm b-t b-r py-2 px-3">{{ categories.filter((cat) => { return cat.id == resp.category_id })[0].name }}</td>
              <td class="text-sm b-t b-r py-2 px-3">{{ resp.score }}</td>
              <td class="text-sm b-t py-2 px-3" v-if="!one_reasoning">{{ resp.text }}</td>
              <td class="text-sm b-t py-2 px-3" v-if="one_reasoning && idx == 0" :rowspan="deletingFeedback.response.responses.length">{{ deletingFeedback.response.feedback }}</td>
            </tr>
          </table>
          <h6 class="py-2">
            After deleting, the reviewer will be able to re-submit their responses provided the assignment is still open.
          </h6>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately and cannot be canceled or reversed!
          </h6>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            If you need to keep a record of these responses, export Feedback data before deleting.
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <label>
            <input type="checkbox" v-model="deleteConfirm" />
            I have already exported Feedback data OR no longer need access to these responses
          </label>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-secondary"
          @click="cancel()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="!deleteConfirm"
          @click="deleteFeedback(deletingFeedback, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <template v-slot:footer>
      <div class="col-6 row no-gutters text-right">
        <div class="dropdown dropdown-menu-right dropup" style="flex:1;">
          <button
            class="btn btn-si-secondary dropdown-toggle"
            type="button"
            id="dropdownMenuButton"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Export
          </button>
          <div class="dropdown-menu text-md" aria-labelledby="dropdownMenuButton">
            <button class="dropdown-item" @click="exportScores">Total Scores</button>
            <button class="dropdown-item" @click="exportFeedback">All Feedback</button>
          </div>
        </div>
        <a class="btn btn-si-secondary ml-3" href="#/pitch_entry">Back</a>
      </div>
    </template>
  </main-page>
</template>

<script>
import MainPage from "@/components/MainPage";
import ConfigSelect from "@/components/ConfigSelect";

import PitchService from "@/services/PitchService";
import ProgramService from "@/services/ProgramService";
import SectionService from "@/services/SectionService";
import FileService from "@/services/FileService";
import Notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import BB from "bluebird";
import _ from "lodash";
import ApexChart from "vue-apexcharts";

var categoryNames = {};

export default {
  name: "PitchProgress",

  props: ["user"],

  components: { MainPage, ConfigSelect, ApexChart },

  data() {
    return {
      fs: fs,
      ld: _,
      loading: true,

      team: null,
      teamName: null,
      category: null,
      type: "scores",
      peer: false,
      educator: true,
      partner: false,
      judge: this.user.role == "JUDGE",
      program: null,

      //Options
      context: null,
      teams: [],
      pitchAssignments: null,
      pitchOptions: [],
      categories: [],
      feedbacks: null,
      allFeedbacks: null,
      deletingFeedback: null,
      deleteConfirm: false,
      explanations: { students: [], teachers: [], partners: [], judges: [] },
      mean: 0,
      median: 0,
      mode: 0,
      rangeLow: 0,
      rangeHigh: 0,

      one_reasoning: false,

      types: [
        {
          label: "Scores",
          id: "scores",
        },
        {
          label: "Explanations",
          id: "explanations",
        },
      ],
      groups: [
        {
          label: "Peer",
          id: "STUDENT",
        },
        {
          label: "Educator",
          id: "TEACHER",
        },
        {
          label: "Community Partner",
          id: "PARTNER",
        },
        {
          label: "Judge",
          id: "JUDGE",
        },
      ],

      chartOptions: {
        chart: {
          id: "chart-pitch-progress",
          zoom: {
            enabled: false,
          },
          toolbar: {
            tools: {
              download: false,
            },
          },
        },
        title: {
          text: "Pitch Progress",
          align: "center",
          style: {
            fontSize: "20px",
          },
        },
        xaxis: {
          categories: ["No Data"],
          labels: {
            show: true,
            style: {
              fontSize: "20px",
            },
          },
          title: {
            text: "Assignments",
            style: {
              fontSize: "20px",
            },
          },
        },
        yaxis: [{
          min: 0,
          max: 100,
          tickAmount: 10,
          forceNiceScale: true,
          decimalsInFloat: 1,
          labels: {
            show: true,
            offsetX: -10,
            style: {
              fontSize: "20px",
            },
          },
          title: {
            text: "Rating",
            style: {
              fontSize: "20px",
            },
          },
        }],
        tooltip: {
          style: {
            fontSize: "20px",
          },
          x: {
            formatter: (value) => {
              let catName = categoryNames[value];
              return catName || value;
            },
          },
        },
        legend: {
          fontSize: "20px",
        },
        colors: [
          "#2554C7",
          "#66DA26",
          "#E42217",
          "#FF9800",
          "#8D38C9",
          "#EA428B",
          "#3BB9FF",
          "#347235",
          "#848482",
          "#EAC117",
        ],
        stroke: {
          curve: "smooth",
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "14px",
            fontWeight: "bold",
          },
          formatter: function (val) {
            if (val == null) {
              return undefined;
            }
            return val.toFixed(1);
          },
        },
        grid: {
          padding: {
            left: 30,
            right: 30,
          },
        },
      },
      chartData: [],
    };
  },

  created() {
    this.loadData();
  },

  watch: {
    team() {
      if (this.team) {
        this.teamName = this.team.name;
      }
      this.loadFeedbacks(this.team).then(() => {
        this.processFeedbacks();
      });
    },
    category() {
      this.processFeedbacks();
    },
    type() {
      if (this.category.id == "all") {
        this.category = this.categoryOptions[0];
      }
      this.teams = _.filter(this.teams, (team) => {
        return !team.all;
      });
      if (this.type != "explanations") {
        this.teams.push({ id: 0, name: "All Teams", all: true });
      }
      this.processFeedbacks();
    },
    peer() {
      this.processFeedbacks();
    },
    educator() {
      this.processFeedbacks();
    },
    partner() {
      this.processFeedbacks();
    },
    judge() {
      this.processFeedbacks();
    },
    categoryOptions() {
      console.log("CATEGORIES CHANGED", this.categoryOptions.length);
    },
  },

  computed: {
    categoryOptions() {
      if (this.type == "scores") {
        return [{ name: "All", id: "all" }].concat(this.categories);
      } else {
        return this.categories;
      }
    },
  },

  methods: {
    loadData() {
      this.loading = true;
      BB.props({
        context: SectionService.getContextPrefilled(),
        program: ProgramService.getContextProgram(),
        feedbacks: PitchService.getAllFeedbacksPrefilled(),
      })
        .then((r) => {
          this.context = r.context.data;
          this.allFeedbacks = r.feedbacks.data;
          if (this.context.teams) {
            this.teams = this.context.teams;
            this.team = this.teams[0];
          }
          _.each(this.teams, (team) => {
            if (!team.name) {
              team.name = this.nameList(team.students);
              team.students = _.filter(team.students, (s) => {
                return this.context.student_ids.includes(s.id);
              });
            }
          });

          this.pitchAssignments = this.context.pitches;

          if (this.user.role == "STUDENT") {
            console.log("DO SELECT OWN ROLE");
            this.selectOwnTeam();

            //remove hidden feedback
            this.pitchAssignments = _.filter(this.context.pitches, {
              hide_feedback: false,
            });
          } else {
            //add all teams option
            this.teams.push({ id: 0, name: "All Teams", all: true });
            this.team = this.teams[this.teams.length - 1];
            this.pitchAssignments = this.context.pitches;
          }

          this.program = r.program.data;
          if (this.context.is_contest) {
            this.categories =
              this.program &&
              this.program.contest_config &&
              this.program.contest_config.rubric &&
              this.program.contest_config.rubric.categories;
          } else {
            this.categories =
              this.program &&
              this.program.pitch_config &&
              this.program.pitch_config.rubric &&
              this.program.pitch_config.rubric.categories;
          }

          if (
            (!this.context.is_contest &&
              this.program.pitch_config &&
              this.program.pitch_config.one_reasoning) ||
            (this.context.is_contest &&
              this.program.contest_config &&
              this.program.contest_config.one_reasoning)
          ) {
            this.one_reasoning = true;
          }

          this.category =
            this.categories && this.categories.length > 0 && this.categories[0];

          this.chartOptions.xaxis.categories = [];
          _.each(this.pitchAssignments, (pa, i) => {
            categoryNames[i + 1] = pa.name;
            this.chartOptions.xaxis.categories.push(i + 1);
          });

          if (this.$refs.chart) {
            this.$refs.chart.updateOptions(this.chartOptions);
          }
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          console.log(e);
          Notie.error("Failed to load data");
        });
    },

    getYMax(cat) {
      var max = 0;
      if (cat.id == "all") {
        for (var c of this.categories) {
          var c_max = this.getYMax(c);
          if (c_max > max) {
            max = c_max;
          }
        }
        return max;
      }

      for (var m of cat.marks) {
        if (m.tick > max) {
          max = m.tick;
        }
      }
      return max;
    },

    selectOwnTeam() {
      for (let team of this.teams) {
        console.log("team", team);
        for (let userID of team.user_ids) {
          console.log("userID", userID, this.user.id);
          if (userID == this.user.id) {
            console.log("SELECT TEAM", team.name);
            this.team = team;
            return;
          }
        }
      }
    },

    loadFeedbacks(team) {
      if (!team) return;

      if (this.user.role == "JUDGE") {
        return  PitchService.getMyFeedbacks()
          .then((r) => {
            if (team.all) {
              this.feedbacks = r.data;
            } else {
              this.feedbacks = r.data.filter((f) => {
                return f.team_id == team.id;
              })
            }
            _.each(this.feedbacks, (f) => {
              f.user = this.user;
            })
          })
      }

      if (team.all) {
        return PitchService.getAllFeedbacksPrefilled()
          .then((r) => {
            this.feedbacks = r.data;
          })
          .catch((e) => {
            console.log(e);
          });
      } else {
        return PitchService.getPitchFeedbacksPrefilled(team.id)
          .then((r) => {
            this.feedbacks = r.data;
          })
          .catch((e) => {
            console.log(e);
            Notie.error("Failed to load feedback data", e);
          });
      }
    },

    getCollatedDataAllTeams(feedbacks) {
      let assignments = {};
      let teamIDs = {};
      _.each(this.pitchAssignments, (assignment) => {
        assignments[assignment.id] = {};
      });
      _.each(feedbacks, (f) => {
        var useFeedback = false;
        switch (f.user && f.user.role) {
          case "STUDENT":
            if (this.peer) {
              useFeedback = true;
            }
            break;

          case "TEACHER":
            if (this.educator) {
              useFeedback = true;
            }
            break;

          case "PARTNER":
            if (this.partner) {
              useFeedback = true;
            }
            break;

          case "JUDGE":
            if (this.judge) {
              useFeedback = true;
            }
            break;
        }

        if (useFeedback) {
          if (!assignments[f.assignment_id]) {
            assignments[f.assignment_id] = {};
          }

          if (!assignments[f.assignment_id][f.team_id]) {
            assignments[f.assignment_id][f.team_id] = [];
          }

          assignments[f.assignment_id][f.team_id].push(f);
          teamIDs[f.team_id] = true;
        }
      });
      return [assignments, Object.keys(teamIDs)];
    },

    getCollatedData(feedbacks) {
      let assignments = {};
      _.each(feedbacks, (f) => {
        if (!assignments[f.assignment_id]) {
          assignments[f.assignment_id] = {
            students: [],
            teachers: [],
            partners: [],
            judges: [],
          };
        }

        switch (f.user && f.user.role) {
          case "STUDENT":
            assignments[f.assignment_id].students.push(f);
            break;
          case "TEACHER":
            assignments[f.assignment_id].teachers.push(f);
            break;
          case "PARTNER":
            assignments[f.assignment_id].partners.push(f);
            break;
          case "JUDGE":
            assignments[f.assignment_id].judges.push(f);
            break;
        }
      });

      return assignments;
    },

    processFeedbacksAll() {
      let [collatedFeedbacks, teamIDs] = this.getCollatedDataAllTeams(this.feedbacks);

      this.chartData = [];
      let scores = {};
      this.explanations = {};
      let unsortedScores = [];

      _.each(this.pitchAssignments, (assignment) => {
        let assignmentExplanations = [];
        let assignmentFeedbacks = collatedFeedbacks[assignment.id];

        _.each(teamIDs, (teamID) => {
          if (!scores[teamID]) {
            scores[teamID] = [];
          }

          if (!assignmentFeedbacks[teamID]) {
            scores[teamID].push(null);
            return;
          }

          let [avgScore, indScores, expListings] = this.getFeedbackSummary(
            assignmentFeedbacks[teamID],
            assignment
          );
          unsortedScores = unsortedScores.concat(indScores);
          scores[teamID].push(avgScore);
          assignmentExplanations = assignmentExplanations.concat(expListings);
        });

        this.explanations[assignment.id] = {
          assignment_name: assignment.name,
          assignment_id: assignment.id,
          explanations: assignmentExplanations,
        };
      });

      _.each(Object.keys(scores), (key) => {
        var team = _.find(this.teams, {
          id: key,
        });
        if (team) {
          this.chartData.push({
            name: team.name,
            data: scores[key],
          });
        }
      });

      [this.mean, this.median, this.mode, this.rangeLow, this.rangeHigh] = fs.statistics(
        unsortedScores
      );
    },

    processFeedbacks() {
      this.chartOptions.yaxis[0].max = this.getYMax(this.category)
      if (this.$refs.chart) {
        this.$refs.chart.updateOptions(this.chartOptions);
      }
      if (this.team && this.team.all) {
        this.processFeedbacksAll();
        return;
      }

      let collatedFeedbacks = this.getCollatedData(this.feedbacks);

      this.chartData = [];
      let scores = {
        students: [],
        teachers: [],
        partners: [],
        judges: [],
      };
      this.explanations = {};
      let unsortedScores = [];

      _.each(this.pitchAssignments, (assignment) => {
        let assignmentExplanations = [];
        let feedbacks = collatedFeedbacks[assignment.id];
        if (!feedbacks) {
          scores.students.push(null);
          scores.teachers.push(null);
          scores.partners.push(null);
          scores.judges.push(null);
          return;
        }

        if (this.peer) {
          let [avgScore, indScores, expListings] = this.getFeedbackSummary(
            feedbacks.students,
            assignment
          );
          unsortedScores = unsortedScores.concat(indScores);
          scores.students.push(avgScore);
          assignmentExplanations = assignmentExplanations.concat(expListings);
        }

        if (this.educator) {
          let [avgScore, indScores, expListings] = this.getFeedbackSummary(
            feedbacks.teachers,
            assignment
          );
          unsortedScores = unsortedScores.concat(indScores);
          scores.teachers.push(avgScore);
          assignmentExplanations = assignmentExplanations.concat(expListings);
        }

        if (this.partner) {
          let [avgScore, indScores, expListings] = this.getFeedbackSummary(
            feedbacks.partners,
            assignment
          );
          unsortedScores = unsortedScores.concat(indScores);
          scores.partners.push(avgScore);
          assignmentExplanations = assignmentExplanations.concat(expListings);
        }

        if (this.judge) {
          let [avgScore, indScores, expListings] = this.getFeedbackSummary(
            feedbacks.judges,
            assignment
          );
          unsortedScores = unsortedScores.concat(indScores);
          scores.judges.push(avgScore);
          assignmentExplanations = assignmentExplanations.concat(expListings);
        }

        this.explanations[assignment.id] = {
          assignment_name: assignment.name,
          assignment_id: assignment.id,
          explanations: assignmentExplanations,
        };
      });

      if (this.peer && !fs.isAllNull(scores.students)) {
        this.chartData.push({
          name: "Peers",
          data: scores.students,
        });
      }

      if (this.educator && !fs.isAllNull(scores.teachers)) {
        this.chartData.push({
          name: "Educators",
          data: scores.teachers,
        });
      }

      if (this.partner && !fs.isAllNull(scores.partners)) {
        this.chartData.push({
          name: "Partners",
          data: scores.partners,
        });
      }

      if (this.judge && !fs.isAllNull(scores.judges)) {
        this.chartData.push({
          name: "Judges",
          data: scores.judges,
        });
      }

      [this.mean, this.median, this.mode, this.rangeLow, this.rangeHigh] = fs.statistics(
        unsortedScores
      );

      this.chartOptions.legend.show = this.chartData.length <= 10;
      if (this.$refs.chart) {
        this.$refs.chart.updateOptions(this.chartOptions);
      }
    },

    getFeedbackSummary(feedbacks, assignment) {
      if (feedbacks.length == 0) {
        return [null, [], []];
      }

      let explanations = [];
      let avgScore = 0;
      let scores = [];
      let num = 0;

      if (this.category.id == "all") {
        _.each(feedbacks, (f) => {
          let responseAvgScore = 0;
          let responseNum = 0;
          _.each(f.response.responses, (r) => {
            num++;
            responseNum++;
            avgScore += r.score;
            responseAvgScore += r.score;

            explanations.push({
              assignment_id: assignment.id,
              assignment_name: assignment.name,
              created_at: r.created_at,
              user: r.user,
              text: r.text,
              score: r.score,
            });
          });
          responseAvgScore = responseAvgScore / responseNum;
          scores.push(responseAvgScore);
        });
      } else {
        _.each(feedbacks, (f) => {
          let catResp = _.find(f.response.responses, {
            category_id: this.category.id,
          });
          if (!catResp) {
            return;
          }

          if (this.one_reasoning) {
            catResp.text = f.response.feedback;
          }

          num++;
          scores.push(catResp.score);
          avgScore += catResp.score;
          explanations.push({
            assignment_id: assignment.id,
            assignment_name: assignment.name,
            created_at: f.created_at,
            user: f.user,
            text: catResp.text,
            score: catResp.score,
          });
        });
      }

      avgScore = avgScore / num;
      return [avgScore, scores, explanations];
    },

    getFeedbackRows(catID) {
      let set = this.feedbacks[catID];
      let feedbacks = [];
      _.each(set, (fb) => {
        if (
          (this.peer && fb.type == "STUDENT") ||
          (this.educator && fb.type == "TEACHER") ||
          (this.partner && fb.type == "PARTNER") ||
          (this.judge && fb.type == "JUDGE")
        ) {
          feedbacks.push(fb);
        }
      });
      return feedbacks;
    },

    getExplanationCountDetails(block) {
      let numStudents = 0;
      let numTeachers = 0;
      let numPartners = 0;
      let numJudges = 0;

      _.each(block.explanations, (e) => {
        if (!e.user) return;

        switch (e.user.role) {
          case "STUDENT":
            numStudents++;
            break;
          case "TEACHER":
            numTeachers++;
            break;
          case "PARTNER":
            numPartners++;
            break;
          case "JUDGE":
            numJudges++;
            break;
        }
      });

      let parts = [];

      if (this.peer) {
        parts.push(`${numStudents} peer`);
      }
      if (this.educator) {
        parts.push(`${numTeachers} educator`);
      }
      if (this.partner) {
        parts.push(`${numPartners} partner`);
      }
      if (this.judge) {
        parts.push(`${numJudges} judge`);
      }

      return parts.join(", ");
    },

    getTitle() {
      if (!this.team) {
        return "(Unknown)";
      }

      let title = this.team.name;

      if (this.category) {
        title += `, ${this.category.name}`;
      }

      return title;
    },

    getFeedbackName(user, index) {
      if (this.user.role == "ADMIN" || this.user.role == "TEACHER" || this.user.role == "JUDGE") {
        return fs.displayName(user.name);
      } else {
        switch (user.role) {
          case "ADMIN":
            return `Educator ${index + 1}`;
          case "TEACHER":
            return `Educator ${index + 1}`;
          case "STUDENT":
            return `Student ${index + 1}`;
          case "PARTNER":
            return `Partner ${index + 1}`;
          case "JUDGE":
            return `Judge ${index + 1}`;
          default:
            return `User ${index + 1}`;
        }
      }
    },

    nameList(users) {
      var displayNames = [];
      for (var user of users) {
        displayNames.push(fs.displayName(user.name));
      }
      return displayNames.join(", ");
    },

    exportFeedback() {
      _.each(this.allFeedbacks, (f) => {
        f.team = _.find(this.teams, {
          id: f.team_id,
        });
        f.assignment = _.find(this.pitchAssignments, {
          id: f.assignment_id,
        });
        _.each(f.response.responses, (r) => {
          r.category = _.find(this.categories, {
            id: r.category_id,
          });
        });
      });

      FileService.exportPitchFeedback(
        this.allFeedbacks.filter((f) => f.assignment),
        this.context,
        this.categories,
        this.user.role == "TEACHER",
        true
      );
    },
    exportScores() {
      var pitchScores = [];

      _.each(this.teams, (team) => {
        _.each(this.pitchAssignments, (a) => {
          var team_feedbacks = _.filter(this.allFeedbacks, (f) => {
            return f.team_id == team.id && f.assignment_id == a.id;
          });

          if (team_feedbacks.length > 0) {
            var scoreObj = {
              team: team.name,
              assignment: a.name,
              scores: {},
            };

            for (var f of team_feedbacks) {
              for (var r of f.response.responses) {
                var cat = _.find(this.categories, {
                  id: r.category_id,
                });
                if (!scoreObj.scores[cat.name]) {
                  scoreObj.scores[cat.name] = [];
                }
                scoreObj.scores[cat.name].push(r.score);
              }
            }

            pitchScores.push(scoreObj);
          }
        });
      });

      FileService.exportPitchScores(pitchScores, this.context, this.categories);
    },

    openDeleteModal(user) {
      this.deletingFeedback = _.filter(this.allFeedbacks, (f) => {
        return f.user_id == user.id;
      })[0];
      this.deleteConfirm = false;
      this.$bvModal.show("deleteFeedbackModal");
    },

    deleteFeedback(feedback) {
      PitchService.deletePitchFeedback(feedback.id)
        .then(() => {
          Notie.success("Successfully deleted");
          this.$bvModal.hide("deleteFeedbackModal");
          this.deletingFeedback = null;
          this.deleteConfirm = false;
          this.loadData();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete Pitch Feedback", e);
        });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.content {
  margin-top: 1rem;
}
.zone-padding {
  padding: 3rem;
}
.splash-button-link > img {
  transition: transform 0.15s ease-in-out;
}
.splash-button-link:hover > img {
  transform: scale(1.05);
}
.splash-button {
  width: 100%;
}
.table-head {
  background-color: #f0f0f0 !important;
  padding: 0.75rem;
  margin-bottom: 1rem;
}
.v-mid {
  vertical-align: middle;
}
.range-description {
  font-size: 0.8rem;
  text-align: center;
}
</style>
